import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-before-bed",
  title: "A prayer before bed — for the night you keep scrolling into",
  h1: "A prayer before bed",
  description:
    "An evening prayer for the hour you meant to be asleep, with four passages on rest, and an honest word about what praying will not fix at midnight.",
  targets: [
    "prayer before bed",
    "night prayer",
    "evening prayer before sleep",
    "prayer to say before sleeping",
  ],
  published: "2026-09-03",
  standfirst:
    "The problem with the end of the day is rarely that you are not tired. It is that going to sleep means stopping, and stopping means the quiet arrives.",
  takeaways: [
    "Say it before you get into bed, not after. Once the phone is in your hand under the covers, the prayer has already lost the argument it was meant to win.",
    "Psalm 4:8 puts the peace before the sleep, not after it — the sleeping is what follows from being held, not the thing being asked for.",
    "If you cannot sleep because your mind is racing rather than because you are stimulated, the prayer is the right tool. If it is the screen, the right tool is a different room.",
  ],
  when: [
    "At the hour you told yourself you would stop, before the next episode starts.",
    "After you have got into bed and picked the phone up again.",
    "When the day ended badly and you are avoiding thinking about it.",
    "On the nights the house is finally quiet and the quiet is the problem.",
  ],
  prayer: [
    "God, the day is over whether or not I am finished with it. I am going to stop now, and stopping is the part I am bad at.",
    "The things I did not get to are still not done. I am leaving them here rather than taking them to bed, because carrying them there has never once helped and I have tested it thoroughly.",
    "Forgive what I did badly today. I am not going to relitigate it in the dark for an hour; I am handing it over and going to sleep.",
    "Keep the house. Keep the people in it. Keep the ones who are not in it and should be.",
    "You do not sleep, so there is no reason for both of us to be awake. Let me put it down.",
  ],
  short: "God, the day is over. I am leaving it with you and going to sleep.",
  honest: [
    "This prayer will not make you sleepy. If the reason you are awake is two hours of bright screen and short video, the body is doing exactly what it has been told to do, and no words fix that at midnight.",
    "It also does not promise a quiet night. Some nights the thing you handed over comes straight back, and the psalms record that happening rather than pretending it does not.",
    "Insomnia that lasts weeks is a medical problem with a real treatment, and treating it is not a failure of trust. Pray, and see somebody.",
  ],
  verses: [
    {
      verse: {
        text: "In peace I will both lay myself down and sleep, for you alone, Yahweh, make me live in safety.",
        ref: "Psalm 4:8",
      },
      note: "Read the order carefully: peace comes first and sleeping follows from it, and the reason given is not that the danger has gone. The psalm's opening verses are a man under pressure from people telling lies about him. He is not safe because the situation resolved. He is safe because of who is holding it while he is unconscious.",
    },
    {
      verse: {
        text: "When you lie down, you will not be afraid. Yes, you will lie down, and your sleep will be sweet.",
        ref: "Proverbs 3:24",
      },
      note: "A proverb, which means a reliable pattern rather than a guarantee — Proverbs describes how life usually goes for the person who lives a certain way, and the book itself knows the exceptions. Taken as a promise it will disappoint you on the first bad night. Taken as a description of what wisdom tends to produce, it is worth aiming at.",
    },
    {
      verse: {
        text: "He will not allow your foot to be moved. He who keeps you will not slumber. Behold, he who keeps Israel will neither slumber nor sleep.",
        ref: "Psalm 121:3–4",
      },
      note: "The comfort here is oddly specific and very practical: somebody is awake, and it does not have to be you. Ancient readers heard a contrast with the gods of the neighbours, who were thought to sleep and need waking. The line is a reason to stop keeping watch yourself, which is exactly what lying awake is.",
    },
    {
      verse: {
        text: "Come to me, all you who labor and are heavily burdened, and I will give you rest.",
        ref: "Matthew 11:28",
      },
      note: "The invitation is to people already carrying something, not to people looking for a nap, and the rest offered in the next two verses comes with a yoke attached — it is the rest of pulling with someone rather than the rest of stopping. Which is a better fit for eleven at night than the greeting-card version.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Why the last hour is the hard one",
      id: "why-the-last-hour-is-the-hard-one",
    },
    {
      type: "p",
      text: "Almost nobody stays up {scrolling} because the content is good. The hour after you should have stopped is the only hour of the day nobody wants anything from you, and giving it up feels like giving up the last thing that was yours. That is a real loss, and pretending it is only weakness makes the advice useless.",
      links: {
        scrolling: "/blog/how-to-stop-scrolling-at-night",
      },
    },
    {
      type: "p",
      text: "So the prayer has to do something other than scold. It marks the end — an actual line, said out loud — and it takes the unfinished day off you before you lie down with it.",
    },
    {
      type: "h2",
      text: "Say it standing up",
      id: "say-it-standing-up",
    },
    {
      type: "p",
      text: "Physically, before you get in. A prayer said in bed with a phone within reach is competing with the phone and will lose most nights. Said while you are still on your feet, plugging the phone in somewhere else, it is the last action of the day rather than an interruption of the first action of the night.",
    },
    {
      type: "note",
      text: "The single most effective change most people make is charging the phone in a different room. It is not a spiritual technique. It just removes the decision from a moment when you are least equipped to make it.",
    },
    {
      type: "h2",
      text: "Leaving the day rather than reviewing it",
      id: "leaving-the-day-rather-than-reviewing-it",
    },
    {
      type: "p",
      text: "There is a long tradition of examining the day before sleep, and it is a good practice done early. Done at midnight it turns into rehearsing the conversation you handled badly, which is not repentance; it is anxiety wearing repentance as a costume.",
    },
    {
      type: "p",
      text: "Name it once, hand it over, stop. If it genuinely needs working through, it will still be there in the morning and you will be better at it then.",
    },
  ],
  faq: [
    {
      q: "What is a good short prayer before sleeping?",
      a: '"God, the day is over. I am leaving it with you and going to sleep." Said standing up, before the phone is plugged in, it works better than a longer one said in bed.',
    },
    {
      q: "Which psalm is best to pray at night?",
      a: "Psalm 4:8 for the words themselves, Psalm 121 if what is keeping you awake is fear about someone else. Both are short enough to say from memory in the dark.",
    },
    {
      q: "Is it wrong to fall asleep while praying?",
      a: "No. Falling asleep mid-sentence in front of someone is what people do in the company of those they trust, and it is a better ending to the day than finishing the prayer and then scrolling for forty minutes.",
    },
  ],
  relatedPrayers: ["prayer-for-anxiety", "morning-prayer"],
  related: ["how-to-stop-scrolling-at-night", "digital-sabbath"],
  relatedTopics: [
    "bible-verses-for-anxiety-at-night",
    "bible-verses-about-rest",
    "bible-verses-about-protection",
  ],
  relatedPassages: ["psalm-23", "psalm-91"],
};
